import { DateTime, formatCalendarDate, ordinalNumber } from "@bps/date-time";
import {
  EventContent,
  NoDataTile,
  Spinner,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { AppointmentVisitDto } from "@libs/gateways/acc/AccGateway.dtos.ts";
import {
  consultEncounterTypes,
  EncounterType,
  TreeView
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ClinicalRecord } from "@stores/clinical/models/ClinicalRecord.ts";
import { Encounter } from "@stores/clinical/models/Encounter.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { ExpandableItem } from "@ui-components/EventList.tsx";

import { ConsultsRemainingBadges } from "./ConsultsRemainingBadges.tsx";

interface ConditionVisitsProps {
  clinicalRecord: ClinicalRecord;
  episodeOfCareId: string;
  patientId: string;
  setVisitCount(value: number): void;
  appointmentVisits?: AppointmentVisitDto[];
}

export const ConditionVisits: React.FC<ConditionVisitsProps> = ({
  clinicalRecord,
  episodeOfCareId,
  patientId,
  setVisitCount,
  appointmentVisits
}) => {
  const { clinical, core } = useStores();
  const theme = useTheme();

  const renderTitle = (
    encounter: Encounter,
    index: number,
    encounters: Encounter[]
  ): React.ReactNode => {
    const showBusinessRoleText =
      core.hasPermissions(Permission.MultiProviderClaimsAllowed) &&
      encounters.some(x =>
        encounters.some(y => y.id !== x.id && y.businessRole !== x.businessRole)
      );

    const businessRole = core.catalogBusinessRoles.find(
      x => x.code === encounter.businessRole
    );

    const businessRoleText =
      showBusinessRoleText && businessRole ? ` - ${businessRole.text}` : "";

    return (
      <>
        <Text
          styles={{
            root: {
              fontWeight: 600,
              color: theme.palette.themeDarker
            }
          }}
        >{`${ordinalNumber(index + 1)} consult${businessRoleText}`}</Text>
        <Text block>{formatCalendarDate(encounter.startDateTime)}</Text>
        {encounter.user && (
          <Text
            block
            styles={(_props, theme) => ({
              root: { color: theme?.palette.neutralPrimaryAlt }
            })}
          >
            {encounter.user.fullName}
          </Text>
        )}
      </>
    );
  };

  const onRowClick = (id: string) => {
    if (clinicalRecord.patientRecordTab) {
      clinicalRecord.patientRecordTab.sidePanelTab = TreeView.PastConsults;
      clinicalRecord.patientRecordTab.sidePanelTabId = id;
    }
  };

  const renderEntries = (encounters: Encounter[]): React.ReactNode => {
    const items = encounters.map((x: Encounter, index: number) => {
      return {
        id: x.id,
        title: renderTitle(x, encounters.length - (index + 1), encounters),
        iconName: "Drop"
      } as ExpandableItem;
    });

    return (
      <Stack
        verticalFill
        grow
        tokens={{ childrenGap: 8 }}
        styles={{ root: { minHeight: "100px", marginLeft: 15 } }}
      >
        {items.map((x: ExpandableItem, index: number) => {
          return (
            <Stack.Item key={x.id + x.key + index.toString()}>
              <EventContent
                iconName={x.iconName}
                id={x.id}
                title={x.title}
                contentHeader={x.contentHeader}
                onRenderContent={x.onRenderContent}
                onClick={onRowClick}
              />
            </Stack.Item>
          );
        })}
      </Stack>
    );
  };

  return (
    <DataFetcher
      fetch={async () => {
        const eocEncounters = await clinical.getEncounters({
          patientId,
          episodeOfCareIds: [episodeOfCareId],
          encounterTypeCodes: consultEncounterTypes
        });

        const finalisedEncounters = eocEncounters.filter(
          x => x.isClosed && x.type !== EncounterType.RecordUpdate
        );

        setVisitCount(finalisedEncounters.length);

        return finalisedEncounters;
      }}
      fallback={<Spinner />}
    >
      {encounters => (
        <div>
          {encounters.length === 0 ? (
            <NoDataTile
              styles={{
                root: { backgroundColor: theme.palette.neutralLighterAlt }
              }}
              textProps={{ text: "No finalised consults" }}
              linkProps={{
                hidden: true
              }}
              showBoxShadow={false}
            />
          ) : (
            <>
              {appointmentVisits && (
                <Stack horizontal wrap tokens={{ childrenGap: 4 }}>
                  <ConsultsRemainingBadges
                    appointmentVisits={appointmentVisits}
                    initialConsultDate={
                      encounters[0].calendarEvent?.claim?.dto
                        ?.initialConsultDate &&
                      `Initial ${DateTime.fromISO(
                        encounters[0].calendarEvent?.claim?.dto
                          ?.initialConsultDate
                      ).toDayDefaultFormat()}`
                    }
                  />
                </Stack>
              )}
              {renderEntries(encounters)}
            </>
          )}
        </div>
      )}
    </DataFetcher>
  );
};
